import React, { useEffect, useState, useRef } from 'react'

import { SelectList } from './SelectList'
import {
  SelectDropdownButtonTrigger,
  ChevronsUpDownIcon,
} from './SelectDropdown.styles'
import { MenuContextProvider } from './SubMenu'

const EMPTY_ARRAY = [] as const
const GRACE_PERIOD_SEARCH = 2000

type DropdownState<T> = {
  active: boolean
  setItem: (item?: T) => void
}

type ItemOptions<T> = DropdownState<T> & {
  a11yProps: {
    id: string
    role: 'option'
    'aria-selected': boolean
    'data-focused': boolean
  }
}

interface SelectDropdownProps<T> {
  className?: string
  placeholder: string
  disabled?: boolean

  id?: string
  name?: string
  autoComplete?: HTMLInputElement['autocomplete']
  value?: T
  items: T[] | ReadonlyArray<T>
  onChange?: (item: T, path: any[]) => void

  renderValueText?: (item: T) => string
  renderButtonText?: (item: T) => React.ReactNode
  renderItem: (item: T, options: ItemOptions<T>) => React.ReactNode

  headerItem?: React.ReactNode
  footerItem?: React.ReactNode
  usePortal?: boolean
}

/**
 * SelectDropdown
 *
 * This component renders a visual looking select that shows either
 * a modal on mobile and a dropdown in tablet/desktop.
 *
 * This components receives a "items" prop that in conjunction with the
 * "renderItem" one represents the items visually on the viewport wrapper
 *
 * Keyboard Support:
 * - Arrow Up/Down: Navigate through options
 * - Home/End: Jump to first/last option
 * - Enter/Space: Select focused option or open dropdown
 * - Escape: Close dropdown
 *
 * Usage:
 * <SelectDropdown
 *    placeholder="Select a fruit..."
 *    items={fruits}
 *    renderItem={(item, props) => <SelectDropdownItem text={item} {...props} />}
 *  />
 */
export function SelectDropdown<T>(props: SelectDropdownProps<T>) {
  const {
    className,
    placeholder,
    value,
    renderItem,
    renderValueText = item => `${item}`,
    renderButtonText = renderValueText,
    items,
    onChange,
    headerItem,
    footerItem,
    disabled,
    usePortal = true,
    id,
    name,
  } = props

  const [expandedState, setExpanded] = useState(false)
  const [forceExpanded, setForceExpanded] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [activePath, setActivePath] = useState<any[]>([])
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  const [search, setSearch] = useState('')

  const expanded = expandedState || forceExpanded
  const visibleItems = expanded ? items : EMPTY_ARRAY

  // If we close the dropdown the button goes back to focus
  useEffect(() => {
    const button = buttonRef.current

    if (expanded) {
      return () => {
        button?.focus()
      }
    }
  }, [expanded])

  // Clear search after "GRACE_PERIOD_SEARCH" of inactivity
  useEffect(() => {
    if (!expanded || search === '') {
      return
    }

    const searchTimeout = setTimeout(() => {
      setSearch('')
    }, GRACE_PERIOD_SEARCH)

    return () => {
      clearTimeout(searchTimeout)
    }
  }, [search, expanded])

  const hasSearch = search !== ''
  // Clear the search if closing the dropdown
  useEffect(() => {
    if (expanded && hasSearch) {
      return () => {
        setSearch('')
      }
    }
  }, [expanded, hasSearch])

  // If the value changed then we need to close the dropdown
  useEffect(() => {
    setExpanded(false)
    setForceExpanded(false)
    setFocusedIndex(-1)
  }, [value])

  useEffect(() => {
    if (!expanded) {
      return
    }

    // Keyboard navigation handler
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setFocusedIndex(prev => {
            const nextIndex = prev < visibleItems.length - 1 ? prev + 1 : prev
            return nextIndex
          })
          break

        case 'ArrowUp':
          event.preventDefault()
          setFocusedIndex(prev => {
            const nextIndex = prev > 0 ? prev - 1 : prev
            return nextIndex
          })
          break

        case 'Home':
          event.preventDefault()
          setFocusedIndex(0)
          break

        case 'End':
          event.preventDefault()
          setFocusedIndex(visibleItems.length - 1)
          break

        case 'Enter':
        case ' ':
          event.preventDefault()
          if (focusedIndex >= 0 && focusedIndex < visibleItems.length) {
            const selectedItem = visibleItems[focusedIndex]
            const path = [selectedItem]
            onChange?.(selectedItem, path)
            setActivePath(path)
            setExpanded(false)
          }
          break

        case 'Escape':
          event.preventDefault()
          setExpanded(false)
          break

        default: {
          event.preventDefault()
          const keyLower = event.key.toLowerCase()

          const itemsTypeFixed = [...visibleItems]
          const itemsStartedByKey = itemsTypeFixed.reduce(
            (acc, item, index) => {
              const label = renderValueText(item).toLowerCase()
              const searchLabel = `${search}${keyLower}`

              if (label.startsWith(searchLabel)) {
                acc.push([index, item])
              }
              return acc
            },
            [] as Array<[number, T]>
          )

          const nextItem = itemsStartedByKey[0]

          if (nextItem) {
            setFocusedIndex(nextItem[0])
            setSearch(prev => `${prev}${keyLower}`)
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [expanded, visibleItems, focusedIndex, onChange, renderValueText, search])

  // Reset focused index when dropdown opens/closes
  useEffect(() => {
    if (expanded) {
      // Find the currently selected item index or start at -1
      const selectedIndex = value
        ? visibleItems.findIndex(item => item === value)
        : -1

      setFocusedIndex(selectedIndex)
    } else {
      setFocusedIndex(-1)
    }
  }, [expanded, value, visibleItems])

  const renderChildren = (item: T, index: number) => {
    const options: ItemOptions<T> = {
      active: item === value,
      setItem: (clickedItem = item) => {
        const path = [clickedItem]
        onChange?.(clickedItem, path)
        setActivePath(path)

        setExpanded(false)
      },
      a11yProps: {
        role: 'option',
        id: `option-${index}`,
        'aria-selected': item === value,
        'data-focused': focusedIndex === index,
      },
    }

    return (
      <React.Fragment key={index}>{renderItem(item, options)}</React.Fragment>
    )
  }

  const children = (
    <div
      role="listbox"
      tabIndex={-1}
      aria-activedescendant={
        focusedIndex >= 0 ? `option-${focusedIndex}` : undefined
      }
    >
      {visibleItems.map(renderChildren)}
    </div>
  )

  const contextSetValue = (item: T, path: any[]) => {
    setExpanded(false)

    if (onChange) {
      onChange?.(item, path)
      setActivePath(path)
    }
  }

  const handleButtonKeyDown = (event: React.KeyboardEvent) => {
    if (['ArrowDown'].includes(event.key) && !expanded) {
      event.preventDefault()
      setExpanded(value => !value)
    }
  }

  return (
    <MenuContextProvider
      value={{
        activePath,
        setValue: contextSetValue,
        setForceExpanded,
        forceExpanded,
      }}
    >
      <SelectDropdownButtonTrigger
        className={className}
        aria-placeholder={placeholder}
        type="button"
        aria-haspopup="listbox"
        aria-valuetext={value && renderValueText(value)}
        aria-expanded={expanded}
        disabled={disabled}
        ref={buttonRef}
        onClick={() => setExpanded(value => !value)}
        onKeyDown={handleButtonKeyDown}
      >
        {value ? renderButtonText(value) : placeholder}
        <ChevronsUpDownIcon />
      </SelectDropdownButtonTrigger>

      <SelectList
        expanded={expanded}
        headerItem={headerItem}
        footerItem={footerItem}
        placement="bottom-start"
        setExpanded={getState => {
          setExpanded(expanded => getState(expanded, buttonRef.current!))
        }}
        usePortal={usePortal}
      >
        {children}
      </SelectList>

      <input
        id={id}
        name={name}
        className="sr-only"
        type="text"
        tabIndex={-1}
        autoComplete={props.autoComplete}
        value={value ? renderValueText(value) : ''}
        onChange={event => {
          const validItem = items.find(
            item => renderValueText(item) === event.target.value
          )

          validItem && onChange?.(validItem, [])
        }}
        onFocus={() => {
          setTimeout(() => {
            buttonRef.current?.focus()
          })
        }}
      />
    </MenuContextProvider>
  )
}
